// @flow
import React, { useState, useEffect } from 'react';
import numeral from 'numeral';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { firstNameInitial } from 'app/utils/displayUtils';
import Checkbox from 'app/components/commons/Checkbox';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Button from 'app/components/commons/Button';

import { TableHead, TableRow, TableCell } from './Table';
import PlayerBar from './PlayerBar';

const TeamTable = ({
  lineUpId,
  selection,
  entered,
  isSaved,
  onRecalculate,
  onRemovePlayer,
  onSaveLineUps,
  onMarkEntered,
  onDeleteLineUp,
  onViewExposure,
  order,
  totalSalary,
  totalScore,
  error,
  sportHasTeam
}: Props) => {
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    onSaveLineUps([
      {
        lineUpId,
        selection
      }
    ]);
  };

  const handleRecalculate = () => {
    onRecalculate(lineUpId);
  };

  useEffect(() => {
    if (error || isSaved) {
      setSaving(false);
    }
  }, [error, isSaved]);

  const hasRemovedPlayer = selection.find(({ isRemoved }) => isRemoved);

  const canSave = !saving && !error && !isSaved && !hasRemovedPlayer;
  const canRecalculate = hasRemovedPlayer;

  const columns = sportHasTeam ? columnsWithTeam : columnsWithoutTteam;

  return (
    <Wrapper>
      <Row>
        <h3>LINEUP {order}</h3>
        <Toolbar>
          <Checkbox checked={entered} onChange={() => onMarkEntered(lineUpId, !entered)}>
            MARK AS ENTERED
          </Checkbox>
          <button type="button" onClick={() => onDeleteLineUp(lineUpId)}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        </Toolbar>
      </Row>
      <TableHead className="is-mobile">
        {columns.map(({ field, label, size }) => (
          <Column key={field} className={size ? `is-${size}` : ''}>
            {label}
          </Column>
        ))}
      </TableHead>
      {selection.map(
        ({
          idStr,
          name,
          team,
          position,
          salary,
          warning,
          score,
          isRemoved,
          labels,
          initials,
          imageURI,
          image
        }) => {
          return isRemoved ? (
            <TableRow key={idStr}>
              <TableCell className="is-12">
                <RemovedDescr className="has-text-centered">
                  Recalculate Lineup to Fill Position
                </RemovedDescr>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow className="is-mobile" key={idStr}>
              <TableCell className="is-6">
                <PlayerBar
                  name={firstNameInitial(name)}
                  labels={labels}
                  image={imageURI || image}
                  initials={initials}
                  warning={warning}
                />
              </TableCell>
              {sportHasTeam && <TableCell className="is-1">{team || '-'}</TableCell>}
              <TableCell className="is-1">{position}</TableCell>
              <TableCell className={sportHasTeam ? 'is-2' : 'is-3'}>
                {numeral(salary).format('$0,0.[00]')}
              </TableCell>
              <TableCell className="is-2">
                <ButtonContainer>
                  <span>{score}</span>
                  <button type="button" onClick={() => onRemovePlayer(lineUpId, idStr)}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </ButtonContainer>
              </TableCell>
            </TableRow>
          );
        }
      )}
      <TableRow>
        <Column className="is-6">Salary Used: {numeral(totalSalary).format('$0,0.[00]')}</Column>
        <Column className="is-6 has-text-right">Total Score: {totalScore}</Column>
      </TableRow>
      <Columns>
        <Button
          autoBorderRadius={false}
          kind={canRecalculate ? 'primary' : 'cancel'}
          disabled={!canRecalculate}
          onClick={() => handleRecalculate(lineUpId)}
          style={{ width: '50%' }}
        >
          RECALCULATE
        </Button>
        <Button
          autoBorderRadius={false}
          kind="info"
          onClick={onViewExposure}
          disabled={!entered}
          style={{ width: '50%' }}
        >
          VIEW EXPOSURE
        </Button>
      </Columns>
      <Columns>
        <Button
          block
          autoBorderRadius={false}
          kind={canSave ? 'primary' : 'cancel'}
          disabled={!canSave}
          onClick={handleSave}
        >
          {saving && <span>Saving</span>}
          {isSaved && (
            <span>
              <FontAwesomeIcon
                icon={faCheckCircle}
                color="green"
                size="lg"
                style={{ marginRight: 4 }}
              />
              Saved In My Teams
            </span>
          )}
          {!saving && !isSaved && <span>MOVE LINEUP TO MY TEAMS</span>}
        </Button>
      </Columns>
      {error && <FormValidationMessage kind="danger" message={error} />}
    </Wrapper>
  );
};

TeamTable.defaultProps = defaultProps;

export default TeamTable;

type Props = {
  onSaveLineUps: (data: Object) => void,
  onRecalculate: (lineUpId: string) => void,
  onDeleteLineUp: (lineUpId: string) => void,
  onMarkEntered: (lineUpId: string) => void,
  onRemovePlayer: (lineUpId: string, playerId: string) => void,
  onViewExposure: (lineUpId: string) => void,
  order: number,
  entered: boolean,
  totalSalary: number,
  totalScore: number,
  lineUpId: string,
  selection: Array<Object>,
  isSaved: boolean,
  error?: string,
  sportHasTeam: boolean
};

const defaultProps = {
  error: undefined
};

const Wrapper = styled.div(({ theme }) => ({
  marginRight: theme.spacing.small,
  marginBottom: theme.spacing.small
}));

const RemovedDescr = styled.span(({ theme }) => ({
  color: theme.color.danger,
  textAlign: 'center',
  minHeight: 47
}));

const columnsWithTeam = [
  {
    field: 'name',
    label: 'NAME',
    size: 6
  },
  {
    field: 'team',
    label: 'TEAM',
    size: 1
  },
  {
    field: 'position',
    label: 'POS',
    size: 1
  },
  {
    field: 'salary',
    label: 'SALARY',
    size: 2
  },
  {
    field: 'score',
    label: 'SCORE',
    size: 2
  }
];

const columnsWithoutTteam = [
  {
    field: 'name',
    label: 'NAME',
    size: 6
  },
  {
    field: 'position',
    label: 'POS',
    size: 1
  },
  {
    field: 'salary',
    label: 'SALARY',
    size: 3
  },
  {
    field: 'score',
    label: 'SCORE',
    size: 2
  }
];

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between'
});

const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const Toolbar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});
